import { useEffect, useRef, useState } from 'react';
import { isTrackReference } from '@livekit/components-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { LocalTrack, RemoteTrack } from 'livekit-client';
import type { Participant } from 'livekit-client';
import { useQualityPrefs } from './quality';
import type { TileOverlay } from './Stage';
import './quality.css';

/**
 * MN-11: a tile's real numbers, read from WebRTC's own stats once a second.
 *
 *   ▼ 1.84 Mb/s  ╱╲╱    what is arriving (▲ on your own tile: what you send)
 *   1280×720 30fps VP8   the layer actually decoded, not the one asked for
 *   loss 0.2% rtt 38ms   the link
 *
 * Nothing here is estimated or echoed back from a setting: bitrate is the byte
 * counter's delta over the sample interval, resolution and fps are what the
 * decoder (or, for you, the top active encoder layer) reports. When the quality
 * menu caps something, this is how a person sees whether the cap took.
 *
 * Shown only while "Stats on tiles" is on in the quality menu; while it is off
 * nothing is polled at all.
 */

const SAMPLE_MS = 1000;
const SPARK_POINTS = 30;

type Sample = {
  dir: 'up' | 'down';
  /** Video bits per second over the last interval. */
  bps: number;
  /** Audio bits per second, this participant's microphone. */
  audioBps?: number;
  width?: number;
  height?: number;
  fps?: number;
  codec?: string;
  /** Percent, over the last interval where counters allow, else since start. */
  loss?: number;
  rttMs?: number;
  jitterMs?: number;
  /** Simulcast layers currently being sent (own tile). */
  layers?: number;
  /** Why the browser is holding the encoder back (own tile), when it is. */
  limit?: string;
  /** Browser's estimate of the upload available (own tile). */
  bweBps?: number;
};

type Counters = { ts: number; bytes: number; lost: number; got: number; audioBytes: number; audioTs: number };

// The stats dictionaries are loosely typed in lib.dom; read them as records.
type Stat = Record<string, unknown> & { type: string; id: string; timestamp: number };
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

function stats(report: RTCStatsReport | undefined): Stat[] {
  const out: Stat[] = [];
  report?.forEach((s) => out.push(s as Stat));
  return out;
}

/** Local and remote tracks both have it; the Track base class does not. */
function reportOf(t: Track | undefined): Promise<RTCStatsReport | undefined> {
  if (t instanceof LocalTrack || t instanceof RemoteTrack) return t.getRTCStatsReport();
  return Promise.resolve(undefined);
}

function codecOf(all: Stat[], codecId: unknown): string | undefined {
  const c = all.find((s) => s.id === codecId);
  return str(c?.mimeType)?.split('/')[1];
}

/** The candidate pair actually carrying media, for RTT and the bandwidth estimate. */
function selectedPair(all: Stat[]): Stat | undefined {
  const transport = all.find((s) => s.type === 'transport');
  const byId = str(transport?.selectedCandidatePairId);
  return (
    (byId && all.find((s) => s.id === byId)) ||
    all.find((s) => s.type === 'candidate-pair' && s.nominated === true && s.state === 'succeeded')
  );
}

async function audioBytes(p: Participant, dir: 'up' | 'down'): Promise<{ bytes: number; ts: number } | undefined> {
  const t = p.getTrackPublication(Track.Source.Microphone)?.track;
  if (!t) return undefined;
  const all = stats(await reportOf(t));
  const rtp = all.find((s) => s.type === (dir === 'down' ? 'inbound-rtp' : 'outbound-rtp'));
  const bytes = num(dir === 'down' ? rtp?.bytesReceived : rtp?.bytesSent);
  return rtp && bytes !== undefined ? { bytes, ts: rtp.timestamp } : undefined;
}

function useTileStats(trackRef: TrackReferenceOrPlaceholder, on: boolean) {
  const [sample, setSample] = useState<Sample | null>(null);
  const [spark, setSpark] = useState<number[]>([]);
  const prev = useRef<Counters | null>(null);

  const track = isTrackReference(trackRef) ? trackRef.publication.track : undefined;
  const participant = trackRef.participant;

  useEffect(() => {
    prev.current = null;
    setSample(null);
    setSpark([]);
    if (!on || !track) return;
    const dir = participant.isLocal ? 'up' : 'down';
    let stopped = false;

    const tick = async () => {
      const all = stats(await reportOf(track));
      const aud = await audioBytes(participant, dir).catch(() => undefined);
      if (stopped) return;

      const s: Sample = { dir, bps: 0 };
      let ts = 0;
      let bytes = 0;
      let lost = 0;
      let got = 0;

      if (dir === 'down') {
        const rtp = all.find((x) => x.type === 'inbound-rtp' && x.kind === 'video');
        if (!rtp) return;
        ts = rtp.timestamp;
        bytes = num(rtp.bytesReceived) ?? 0;
        lost = num(rtp.packetsLost) ?? 0;
        got = num(rtp.packetsReceived) ?? 0;
        s.width = num(rtp.frameWidth);
        s.height = num(rtp.frameHeight);
        s.fps = num(rtp.framesPerSecond);
        s.codec = codecOf(all, rtp.codecId);
        const j = num(rtp.jitter);
        if (j !== undefined) s.jitterMs = j * 1000;
      } else {
        // One outbound-rtp per simulcast layer. Bytes are their sum; the size
        // shown is the biggest layer actually going out right now.
        const layers = all.filter((x) => x.type === 'outbound-rtp' && x.kind === 'video');
        if (layers.length === 0) return;
        ts = Math.max(...layers.map((x) => x.timestamp));
        bytes = layers.reduce((a, x) => a + (num(x.bytesSent) ?? 0), 0);
        const live = layers.filter((x) => x.active !== false && num(x.frameWidth));
        const top = live.reduce<Stat | undefined>(
          (best, x) => ((num(x.frameWidth) ?? 0) > (num(best?.frameWidth) ?? 0) ? x : best),
          undefined,
        );
        s.layers = live.length;
        s.width = num(top?.frameWidth);
        s.height = num(top?.frameHeight);
        s.fps = num(top?.framesPerSecond);
        s.codec = codecOf(all, top?.codecId ?? layers[0]?.codecId);
        const lim = str(top?.qualityLimitationReason);
        if (lim && lim !== 'none') s.limit = lim;
        // What the far end says it lost of ours, per layer; the worst one.
        const remote = all.filter((x) => x.type === 'remote-inbound-rtp' && x.kind === 'video');
        const fl = remote.map((x) => num(x.fractionLost)).filter((v): v is number => v !== undefined);
        if (fl.length) s.loss = Math.max(...fl) * 100;
        const rr = remote.map((x) => num(x.roundTripTime)).filter((v): v is number => v !== undefined);
        if (rr.length) s.rttMs = Math.min(...rr) * 1000;
      }

      const pair = selectedPair(all);
      const prt = num(pair?.currentRoundTripTime);
      if (prt !== undefined) s.rttMs = prt * 1000;
      if (dir === 'up') s.bweBps = num(pair?.availableOutgoingBitrate);

      const p = prev.current;
      if (p && ts > p.ts) {
        s.bps = ((bytes - p.bytes) * 8 * 1000) / (ts - p.ts);
        if (dir === 'down') {
          const dl = lost - p.lost;
          const dg = got - p.got;
          s.loss = dl + dg > 0 ? (Math.max(0, dl) / (dl + dg)) * 100 : 0;
        }
        if (aud && p.audioTs && aud.ts > p.audioTs) {
          s.audioBps = ((aud.bytes - p.audioBytes) * 8 * 1000) / (aud.ts - p.audioTs);
        }
      }
      prev.current = { ts, bytes, lost, got, audioBytes: aud?.bytes ?? 0, audioTs: aud?.ts ?? 0 };
      // The first sample has no interval to measure over; show nothing rather
      // than a zero that looks like a dead stream.
      if (!p) return;
      setSample(s);
      setSpark((xs) => [...xs, s.bps].slice(-SPARK_POINTS));
    };

    const t = window.setInterval(() => {
      tick().catch(() => undefined);
    }, SAMPLE_MS);
    void tick().catch(() => undefined);
    return () => {
      stopped = true;
      window.clearInterval(t);
    };
  }, [on, track, participant]);

  return { sample, spark };
}

function rate(bps: number): [string, string] {
  if (bps >= 1e6) return [(bps / 1e6).toFixed(2), 'Mb/s'];
  return [Math.round(bps / 1e3).toString(), 'kb/s'];
}

function Spark({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const max = Math.max(...points, 1);
  const w = 48;
  const h = 12;
  const d = points
    .map((v, i) => `${((i / (SPARK_POINTS - 1)) * w).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="mx-q-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={d} fill="none" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function StatsReadout({ trackRef }: { trackRef: TrackReferenceOrPlaceholder }) {
  const { stats: on } = useQualityPrefs();
  const { sample: s, spark } = useTileStats(trackRef, on);
  if (!on || !s) return null;

  const [v, unit] = rate(s.bps);
  const link: string[] = [];
  if (s.loss !== undefined) link.push(`loss ${s.loss.toFixed(s.loss < 10 ? 1 : 0)}%`);
  if (s.rttMs !== undefined) link.push(`rtt ${Math.round(s.rttMs)}ms`);
  if (s.jitterMs !== undefined) link.push(`jit ${Math.round(s.jitterMs)}ms`);

  const media: string[] = [];
  if (s.width && s.height) media.push(`${s.width}×${s.height}`);
  if (s.fps !== undefined) media.push(`${Math.round(s.fps)}fps`);
  if (s.codec) media.push(s.codec);

  const extra: string[] = [];
  if (s.audioBps !== undefined) extra.push(`aud ${rate(s.audioBps).join(' ')}`);
  if (s.layers !== undefined) extra.push(`${s.layers} layer${s.layers === 1 ? '' : 's'}`);
  if (s.bweBps !== undefined) extra.push(`bwe ${rate(s.bweBps).join(' ')}`);

  return (
    <div
      className="mx-q-readout"
      data-dir={s.dir}
      data-limited={s.limit ? 'true' : undefined}
      aria-label={`${s.dir === 'up' ? 'Sending' : 'Receiving'} ${v} ${unit}`}
    >
      <div className="mx-q-r1">
        <span className="mx-q-arrow" aria-hidden="true">
          {s.dir === 'up' ? '▲' : '▼'}
        </span>
        <b>{v}</b>
        <i>{unit}</i>
        <Spark points={spark} />
      </div>
      {media.length > 0 && <div className="mx-q-r2">{media.join(' ')}</div>}
      {link.length > 0 && <div className="mx-q-r3">{link.join(' ')}</div>}
      {extra.length > 0 && <div className="mx-q-r3">{extra.join(' · ')}</div>}
      {s.limit && <div className="mx-q-r3 mx-q-warn">limited: {s.limit}</div>}
    </div>
  );
}

/** For CallStage's `tileOverlay` slot: `<CallStage tileOverlay={statsOverlay} />`. */
export const statsOverlay: TileOverlay = (trackRef) => <StatsReadout trackRef={trackRef} />;
